/* eslint-disable i18next/no-literal-string -- a code-only module: every literal is a DOM event name, a fetch option or a state code; it renders nothing */
// C1 Connectivity for the web: hosts the pure machine
// (connectivityMachine.ts). Inputs: navigator.onLine and the window
// online/offline events (the OS path, debounced by the machine), every API
// request's answer (apiFetch -> connectivityFeed), and a single-flight probe
// of GET /health/live (3 s timeout, no-store). Emits went_offline, degraded
// and `recovered` (every offline/degraded -> online), which the C1b
// scheduler hangs every drain off.
//
// No browser global at module scope; start() wires the listeners.
import { useSyncExternalStore } from 'react'
import { ConnectivityMachine, PROBE_TIMEOUT_MS, type Effect } from './connectivityMachine'
import { setFeedEpoch, setFeedSink } from './connectivityFeed'
import { CONN, CONN_EVENT, PROBE_PATH, type ConnectivityEvent, type ConnectivityState } from './offline.codes'
import { apiUrl } from '../../api/apiUrl'
import { apiFetchUnreported } from '../../i18n/apiFetch'

let machine: ConnectivityMachine | null = null
let state: ConnectivityState = CONN.online
let started = false
let timer: ReturnType<typeof setTimeout> | null = null
let probeAbort: AbortController | null = null
let probeImpl: (() => Promise<boolean>) | null = null
let clock: () => number = () => Date.now()
const stateListeners = new Set<() => void>()
const eventListeners = new Set<(e: ConnectivityEvent) => void>()
const foregroundListeners = new Set<() => void>()
const teardown: Array<() => void> = []

export function connectivityState(): ConnectivityState {
  return state
}

export function isApiReachable(): boolean {
  return state === CONN.online
}

export function subscribeConnectivityState(fn: () => void): () => void {
  stateListeners.add(fn)
  return () => { stateListeners.delete(fn) }
}

/** recovered / went_offline / degraded. */
export function onConnectivityEvent(fn: (e: ConnectivityEvent) => void): () => void {
  eventListeners.add(fn)
  return () => { eventListeners.delete(fn) }
}

/** The tab became visible (the scheduler's `foreground` trigger). */
export function onForeground(fn: () => void): () => void {
  foregroundListeners.add(fn)
  return () => { foregroundListeners.delete(fn) }
}

export function useConnectivity(): ConnectivityState {
  return useSyncExternalStore(subscribeConnectivityState, connectivityState, () => CONN.online)
}

/**
 * Counts every transition INTO online (offline/degraded -> online). Read on
 * demand, so a consumer that was not mounted when the recovery happened
 * (the card image fallback) still sees it. Monotonic: never reset, not even
 * by the test reset, so a remembered generation can never match by accident.
 */
let recoveryGen = 0

export function connectivityRecoveryGen(): number {
  return recoveryGen
}

function setState(next: ConnectivityState): void {
  if (next === state) return
  if (next === CONN.online) recoveryGen++
  state = next
  for (const l of [...stateListeners]) l()
}

function apply(effects: Effect[]): void {
  const m = machine
  if (!m) return
  setFeedEpoch(m.pathEpoch)
  for (const e of effects) {
    if (e.kind === 'transition') {
      setState(e.transition.to)
      if (e.transition.emits) for (const l of [...eventListeners]) l(e.transition.emits)
    } else {
      runProbe(e.epoch)
    }
  }
  if (!m.probeInFlight && probeAbort) {
    probeAbort.abort()
    probeAbort = null
  }
  armTimer()
}

function armTimer(): void {
  if (timer) clearTimeout(timer)
  timer = null
  const m = machine
  const deadline = m?.nextDeadline
  if (!m || deadline === null || deadline === undefined) return
  timer = setTimeout(() => {
    timer = null
    if (machine) apply(machine.advance(clock()))
  }, Math.max(0, deadline - clock()))
}

/** GET {API}/health/live, no-store, 3,000 ms timeout, success = HTTP 200. */
async function liveProbe(ac: AbortController): Promise<boolean> {
  const t = setTimeout(() => ac.abort(), PROBE_TIMEOUT_MS)
  try {
    const res = await apiFetchUnreported(apiUrl(PROBE_PATH), { cache: 'no-store', headers: { 'Cache-Control': 'no-store' }, signal: ac.signal })
    return res.status === 200
  } catch {
    return false
  } finally {
    clearTimeout(t)
  }
}

function runProbe(epoch: number): void {
  probeAbort?.abort()
  const ac = new AbortController()
  probeAbort = ac
  const p = probeImpl ? probeImpl() : liveProbe(ac)
  void p.then(ok => {
    if (ac.signal.aborted && probeAbort !== ac) return
    if (probeAbort === ac) probeAbort = null
    if (machine) apply(machine.probeResult(ok, epoch, clock()))
  })
}

/**
 * Start C1 (idempotent). The initial state is `offline` if the browser says
 * so, else `online` optimistically with a probe at once (no `recovered`).
 */
export function startConnectivity(opts: { probe?: () => Promise<boolean>; now?: () => number } = {}): void {
  if (started || typeof window === 'undefined') return
  started = true
  if (opts.probe) probeImpl = opts.probe
  if (opts.now) clock = opts.now
  const satisfied = typeof navigator === 'undefined' || navigator.onLine !== false
  machine = new ConnectivityMachine(satisfied ? CONN.online : CONN.offline)
  state = machine.state
  setFeedSink((success, epoch) => { if (machine) apply(machine.apiResult(success, epoch, clock())) })
  const on = () => { if (machine) apply(machine.osPath(true, clock())) }
  const off = () => { if (machine) apply(machine.osPath(false, clock())) }
  const vis = () => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
    if (machine) apply(machine.foreground(clock()))
    for (const l of [...foregroundListeners]) l()
  }
  window.addEventListener('online', on)
  window.addEventListener('offline', off)
  document.addEventListener('visibilitychange', vis)
  teardown.push(() => window.removeEventListener('online', on), () => window.removeEventListener('offline', off), () => document.removeEventListener('visibilitychange', vis))
  for (const l of [...stateListeners]) l()
  if (satisfied) apply(machine.probeNow(clock()))
}

/** Test seam: force a state (emitting what the transition would). */
export function __setConnectivityForTests(next: ConnectivityState, emit = false): void {
  const was = state
  setState(next)
  if (emit && next === CONN.online && was !== CONN.online) for (const l of [...eventListeners]) l(CONN_EVENT.recovered)
}

export function __resetConnectivityForTests(): void {
  for (const t of teardown.splice(0)) t()
  if (timer) clearTimeout(timer)
  timer = null
  probeAbort?.abort()
  probeAbort = null
  probeImpl = null
  clock = () => Date.now()
  machine = null
  started = false
  state = CONN.online
  setFeedSink(null)
  stateListeners.clear()
  eventListeners.clear()
  foregroundListeners.clear()
}
